/* eslint-disable no-unused-vars, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, consistent-return, @typescript-eslint/strict-boolean-expressions, @typescript-eslint/no-unnecessary-condition */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { NextRequest } from "next/server";
import { getAuthInstance, getSession } from "../utils/auth0";
import { isLocalDev } from "../utils/is-local-dev";
import { clearLegacySessionCookies } from "../utils/legacy-cookies";
import { getProfileCache } from "../utils/profile-cache";
import { getServerRuntimeConfig } from "../utils/runtime-config";
import { handleAccountData } from "./accountData";
import { handleIdentityCreateEntitlements } from "./identityCreateEntitlements";
import { handleIdentityEntitlements } from "./identityEntitlements";
import { handleIdentityProfile } from "./identityProfile";
import { handleIdentityResetPassword } from "./identityResetPassword";
import { handleIdentitySettings } from "./identitySettings";
import { handleIdentityUpdateEntitlements } from "./identityUpdateEntitlements";
import { handleIdentityUpdateProfile } from "./identityUpdateProfile";
/**
 * Convert Pages Router API request to Web Request for v4 middleware.
 * Optionally accepts custom returnTo and connection params for login flows.
 * V4 middleware respects these query parameters.
 */
const convertToWebRequest = (req, route, options) => {
    var _a, _b, _c;
    const protoHeader = req.headers["x-forwarded-proto"];
    const protocol = Array.isArray(protoHeader)
        ? (_a = protoHeader[0]) !== null && _a !== void 0 ? _a : "http"
        : protoHeader !== null && protoHeader !== void 0 ? protoHeader : "http";
    const host = (_b = req.headers.host) !== null && _b !== void 0 ? _b : "localhost:3000";
    // Build the URL for the auth route
    // The v4 middleware expects routes at /auth/*
    const url = new URL(`${protocol}://${host}/auth/${route}`);
    // Set custom params if provided
    if (options === null || options === void 0 ? void 0 : options.returnTo) {
        url.searchParams.set("returnTo", options.returnTo);
    }
    if (options === null || options === void 0 ? void 0 : options.connection) {
        url.searchParams.set("connection", options.connection);
    }
    // Keys to skip when copying query params
    const skipKeys = new Set(["auth0"]);
    if (options === null || options === void 0 ? void 0 : options.returnTo) {
        skipKeys.add("returnTo");
    }
    if (options === null || options === void 0 ? void 0 : options.connection) {
        skipKeys.add("connection");
        skipKeys.add("conn");
    }
    // Copy query parameters (except skipped keys)
    Object.entries(req.query).forEach(([key, value]) => {
        var _a;
        if (!skipKeys.has(key) && value !== undefined) {
            const strValue = Array.isArray(value) ? (_a = value[0]) !== null && _a !== void 0 ? _a : "" : value;
            if (strValue) {
                url.searchParams.set(key, strValue);
            }
        }
    });
    // Build headers
    const headers = new Headers();
    Object.entries(req.headers).forEach(([key, value]) => {
        if (value !== undefined) {
            const headerValue = Array.isArray(value) ? value.join(", ") : value;
            headers.set(key, headerValue);
        }
    });
    return new NextRequest(url, {
        method: (_c = req.method) !== null && _c !== void 0 ? _c : "GET",
        headers,
    });
};
/**
 * Apply NextResponse to Pages Router response
 */
const applyNextResponse = (nextResponse, res) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    // Collect all Set-Cookie headers (there can be multiple)
    const cookies = [];
    nextResponse.headers.forEach((value, key) => {
        if (key.toLowerCase() === "set-cookie") {
            // Split if multiple cookies in one header
            cookies.push(value);
        }
    });
    // Set cookies FIRST (critical for session to work)
    if (cookies.length > 0) {
        res.setHeader("Set-Cookie", cookies);
    }
    // Copy other headers
    nextResponse.headers.forEach((value, key) => {
        const lowerKey = key.toLowerCase();
        if (lowerKey !== "set-cookie" && lowerKey !== "content-encoding") {
            res.setHeader(key, value);
        }
    });
    // Handle redirect - don't use res.redirect() as it can override headers
    if (nextResponse.status >= 300 && nextResponse.status < 400) {
        const location = nextResponse.headers.get("location");
        if (location !== null && location !== "") {
            res.status(nextResponse.status);
            res.setHeader("Location", location);
            res.end();
            return;
        }
    }
    // Copy status and body
    res.status(nextResponse.status);
    const body = yield nextResponse.text();
    if (body) {
        try {
            const jsonBody = JSON.parse(body);
            res.json(jsonBody);
        }
        catch (_b) {
            const contentType = (_a = nextResponse.headers.get("content-type")) !== null && _a !== void 0 ? _a : "";
            if (contentType.includes("application/json")) {
                res.status(500).json({ error: "Invalid response format" });
            }
            else {
                res.end();
            }
        }
    }
    else {
        res.end();
    }
});
/**
 * Main auth handler for Pages Router.
 *
 * This handles:
 * 1. Custom DTN endpoints (users, identityProfile, etc.)
 * 2. Auth routes for backward compatibility (redirect to /auth/* in v4)
 *
 * @param userHandlers - Optional custom handlers (for backward compatibility)
 */
export const handleAuth = (_userHandlers) => {
    return (req, res) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f;
        const auth = yield getAuthInstance(req);
        // Extract the route from the catch-all parameter
        let { query: { auth0: route }, } = req;
        route = Array.isArray(route) ? route[0] : route;
        // =========================================================================
        // CUSTOM DTN ENDPOINTS (preserved from original)
        // =========================================================================
        if (route === "users") {
            // DEPRECATED: This endpoint is no longer supported in v4
            // eslint-disable-next-line no-console
            console.warn("[DEPRECATED] /auth/users endpoint is deprecated and will be removed. Please migrate to a direct identity service integration.");
            res.status(410).json({
                error: "Gone",
                message: "The /auth/users endpoint is deprecated and no longer available. Please contact the platform team for migration guidance.",
                deprecated: true,
            });
            return;
        }
        if (route === "identityProfile") {
            return handleIdentityProfile(req, res);
        }
        if (route === "identityEntitlements") {
            return handleIdentityEntitlements(req, res);
        }
        if (route === "identitySettings") {
            return handleIdentitySettings(req, res);
        }
        if (route === "updateProfile") {
            return handleIdentityUpdateProfile(req, res);
        }
        if (route === "resetPassword") {
            return handleIdentityResetPassword(req, res);
        }
        if (route === "updateEntitlements") {
            return handleIdentityUpdateEntitlements(req, res);
        }
        if (route === "createEntitlements") {
            return handleIdentityCreateEntitlements(req, res);
        }
        if (route === "identityAccount") {
            return handleAccountData(req, res);
        }
        // DEPRECATED: Old portalToken endpoint
        if (route === "portalToken") {
            res.status(410).json({
                error: "This endpoint has been removed for security reasons. Use /auth/profile instead.",
            });
            return;
        }
        // =========================================================================
        // SESSION ENDPOINT (updated for v4)
        // =========================================================================
        if (route === "session") {
            try {
                const session = (yield getSession(req));
                // V4: Token expiration is at session.tokenSet.expiresAt
                const tokenSet = session === null || session === void 0 ? void 0 : session.tokenSet;
                const exp = tokenSet === null || tokenSet === void 0 ? void 0 : tokenSet.expiresAt;
                res.status(200).json({ timestamp: exp !== null && exp !== void 0 ? exp : null });
            }
            catch (error) {
                // Handle invalid session (e.g., JWEInvalid after SDK upgrade)
                const errorCode = error.code;
                if (errorCode === "ERR_JWE_INVALID" ||
                    errorCode === "ERR_JWE_DECRYPTION_FAILED") {
                    // Session cookie is invalid/corrupted, return null timestamp
                    res.status(200).json({ timestamp: null });
                    return;
                }
                res.status((_a = error.status) !== null && _a !== void 0 ? _a : 500).end();
            }
            return;
        }
        // =========================================================================
        // ENVIRONMENT INFO ENDPOINT (preserved)
        // =========================================================================
        if (route === "idAppEnv") {
            const { DTN_IDENTITY_HOST } = getServerRuntimeConfig();
            const host = DTN_IDENTITY_HOST !== null && DTN_IDENTITY_HOST !== void 0 ? DTN_IDENTITY_HOST : "";
            let identityAppEnv = "prd";
            if (host.includes("gateway.dev.coreservices")) {
                identityAppEnv = "dev";
            }
            else if (host.includes("gateway.stg.coreservices")) {
                identityAppEnv = "stg";
            }
            res.status(200).json({ identityAppEnv });
            return;
        }
        // =========================================================================
        // PROFILE ENDPOINT (for backward compatibility)
        // V4: Returns user with custom claims from ID token
        // =========================================================================
        if (route === "me" || route === "profile") {
            try {
                let session;
                try {
                    session = yield getSession(req);
                }
                catch (sessionError) {
                    // Handle invalid session (e.g., JWEInvalid after SDK upgrade)
                    const errorCode = sessionError.code;
                    if (errorCode === "ERR_JWE_INVALID" ||
                        errorCode === "ERR_JWE_DECRYPTION_FAILED") {
                        // eslint-disable-next-line no-console
                        console.warn("[AUTH_PROFILE] Invalid session cookie, user needs to re-login");
                        // 204 No Content - client will treat as "not logged in"
                        res.status(204).end();
                        return;
                    }
                    throw sessionError;
                }
                const userObj = session === null || session === void 0 ? void 0 : session.user;
                if (!session || typeof (userObj === null || userObj === void 0 ? void 0 : userObj.sub) !== "string") {
                    // No session = not logged in, return 204 No Content
                    // This prevents redirect loops with Auth0Provider
                    res.status(204).end();
                    return;
                }
                const userId = userObj.sub;
                const cache = getProfileCache();
                // Check cache first
                const cached = cache.get(userId);
                if (cached !== null) {
                    res.status(200).json(cached);
                    return;
                }
                // beforeSessionSaved passthrough ensures session.user already has all claims
                const userWithClaims = session.user;
                // Cache and return
                cache.set(userId, userWithClaims);
                res.status(200).json(userWithClaims);
            }
            catch (error) {
                res.status((_b = error.status) !== null && _b !== void 0 ? _b : 500).end();
            }
            return;
        }
        // =========================================================================
        // AUTH ROUTES (handled by v4 middleware with custom logic)
        // =========================================================================
        if (route === "login" || route === "logout" || route === "callback") {
            if (!auth) {
                res.status(500).json({ error: "Auth0 instance not initialized" });
                return;
            }
            try {
                // =====================================================================
                // CUSTOM LOGIN LOGIC
                // Handles domain validation, returnTo URL, and connection params
                // =====================================================================
                if (route === "login") {
                    const appDomain = (_d = (_c = process.env.DTN_APP_DOMAIN) !== null && _c !== void 0 ? _c : process.env.APP_BASE_URL) !== null && _d !== void 0 ? _d : "";
                    const requestHost = (_e = req.headers.host) !== null && _e !== void 0 ? _e : "";
                    const isLocalhostHost = isLocalDev(requestHost);
                    const requestProtocol = isLocalhostHost ? "http" : "https";
                    const requestOrigin = requestHost.length > 0
                        ? `${requestProtocol}://${requestHost}`
                        : appDomain;
                    // Validate that a URL is from a valid subdomain
                    const isValidDomain = (url) => {
                        try {
                            const urlObj = new URL(url);
                            const baseDomain = new URL(appDomain).hostname;
                            const { hostname } = urlObj;
                            return (hostname === baseDomain || hostname.endsWith(`.${baseDomain}`));
                        }
                        catch (_a) {
                            return false;
                        }
                    };
                    // Generate returnTo URL with optional connection param
                    const generateReturnTo = (host) => {
                        const pathValue = typeof req.query.path === "string" ? req.query.path : "/";
                        return `${host}${pathValue}`;
                    };
                    // Determine returnTo based on referer, request host, or app domain
                    let returnTo;
                    if (Boolean(req.headers.referer) &&
                        isValidDomain(req.headers.referer)) {
                        returnTo = req.headers.referer;
                    }
                    else if (Boolean(requestHost) && isValidDomain(requestOrigin)) {
                        returnTo = generateReturnTo(requestOrigin);
                    }
                    else {
                        returnTo = generateReturnTo(appDomain);
                    }
                    // Extract connection parameter for custom identity providers
                    const connParam = (_f = req.query.connection) !== null && _f !== void 0 ? _f : req.query.conn;
                    const connValue = Array.isArray(connParam) ? connParam[0] : connParam;
                    const connection = typeof connValue === "string" && connValue.trim().length > 0
                        ? connValue.trim()
                        : undefined;
                    // Build URL with custom returnTo and connection params
                    // V4 middleware respects these query parameters
                    const webRequest = convertToWebRequest(req, route, {
                        returnTo,
                        connection,
                    });
                    const response = (yield auth.middleware(webRequest));
                    yield applyNextResponse(response, res);
                    return;
                }
                // =====================================================================
                // CUSTOM LOGOUT LOGIC
                // Clears profile cache before logout
                // =====================================================================
                if (route === "logout") {
                    try {
                        const logoutSession = (yield getSession(req));
                        const logoutUser = logoutSession === null || logoutSession === void 0 ? void 0 : logoutSession.user;
                        if (logoutUser === null || logoutUser === void 0 ? void 0 : logoutUser.sub) {
                            const cache = getProfileCache();
                            cache.invalidate(logoutUser.sub);
                        }
                    }
                    catch (_g) {
                        // Ignore errors during cache invalidation
                    }
                    const webRequest = convertToWebRequest(req, route);
                    const response = (yield auth.middleware(webRequest));
                    // Append legacy v3 "appSession" cookie clearing headers before applying the response.
                    const legacyCookies = clearLegacySessionCookies();
                    for (const cookie of legacyCookies) {
                        response.headers.append("set-cookie", cookie);
                    }
                    yield applyNextResponse(response, res);
                    return;
                }
                // =====================================================================
                // CALLBACK
                // =====================================================================
                const webRequest = convertToWebRequest(req, route);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                const response = (yield auth.middleware(webRequest));
                // Apply the response to the Pages Router response
                yield applyNextResponse(response, res);
            }
            catch (error) {
                // eslint-disable-next-line no-console
                console.error(`[AUTH_${route.toUpperCase()}_ERROR]`, error);
                res.status(500).json({ error: `Auth ${route} failed` });
            }
            return;
        }
        res.status(404).json({ error: `Unknown auth route: ${route}` });
    });
};
export default handleAuth();
